// Location: src/features/labels/LabelChip.tsx
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const DEFAULT_COLOR = '#0BA5B7';

/** Pick a readable text colour (black/white) for a solid background hex. */
function readableText(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return '#fff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? 'rgba(0,0,0,0.82)' : '#fff';
}

export interface LabelChipProps {
  scope?: string | null;
  value: string;
  color?: string | null;
  onDelete?: () => void;
  size?: 'small' | 'medium';
  title?: string;
  /** Show only the value (the scope/key stays in the tooltip). Saves space in dense
   * places like the activity monitor cards. */
  valueOnly?: boolean;
}

/**
 * GitLab-style label chip. A scoped label renders two segments (key | value);
 * a free label renders a single tinted segment. With `valueOnly`, a scoped label
 * collapses to just its value (the key remains in the tooltip). Purely presentational.
 */
export const LabelChip = ({ scope, value, color, onDelete, size = 'small', title, valueOnly }: LabelChipProps) => {
  const bg = color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : DEFAULT_COLOR;
  const fg = readableText(bg);
  const tintBg = `${bg}26`; // ~15% alpha tint
  const height = size === 'small' ? 20 : 24;
  const fontSize = size === 'small' ? '0.7rem' : '0.78rem';

  return (
    <Box
      title={title ?? (scope ? `${scope}: ${value}` : value)}
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        borderRadius: 1,
        overflow: 'hidden',
        height,
        fontSize,
        lineHeight: `${height}px`,
        maxWidth: '100%',
        border: `1px solid ${bg}55`,
      }}
    >
      {scope && !valueOnly ? (
        <>
          <Box sx={{ px: 0.75, bgcolor: bg, color: fg, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {scope}
          </Box>
          <Box
            sx={{
              px: 0.75,
              bgcolor: tintBg,
              color: 'text.primary',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            px: 0.75,
            bgcolor: scope ? tintBg : bg,
            color: scope ? 'text.primary' : fg,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </Box>
      )}
      {onDelete && (
        <IconButton
          size="small"
          onClick={onDelete}
          aria-label="delete"
          sx={{
            alignSelf: 'stretch',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            width: height,
            borderRadius: 0,
            color: 'text.secondary',
            '&:hover': { color: 'error.main', bgcolor: 'transparent' },
          }}
        >
          <CloseIcon sx={{ fontSize: size === 'small' ? 15 : 17 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default LabelChip;
